import React, { useState } from 'react';
import { recordBloodDonation } from '../hooks/useSupabase';
import { KERALA_HOSPITALS } from '../data/keralaHospitals';

const DonationRecorder = ({ donor, onDonationRecorded }) => {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    donationDate: '',
    hospital: '',
    hospitalLocation: '',
    units: 1,
    urgency: 'normal',
    isEmergency: false,
    notes: '',
    certificateNumber: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const donationData = {
        ...formData,
        donationDate: new Date(formData.donationDate),
        units: parseInt(formData.units),
        donorBloodGroup: donor.bloodGroup
      };

      const result = await recordBloodDonation(donor.id, donationData);
      
      alert(`Donation recorded successfully! You earned ${result.pointsEarned} points! 🎉`);
      
      if (onDonationRecorded) {
        onDonationRecorded(result);
      }
      
      setShowForm(false);
      setFormData({
        donationDate: '',
        hospital: '',
        hospitalLocation: '',
        units: 1,
        urgency: 'normal',
        isEmergency: false,
        notes: '',
        certificateNumber: ''
      });
    } catch (error) {
      console.error('Error recording donation:', error);
      alert('Failed to record donation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (!showForm) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">🩸 Record Blood Donation</h3>
            <p className="text-sm text-gray-600 mt-1">
              Did you donate blood recently? Record it here to earn points and track your donations!
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Record Donation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">🩸 Record Blood Donation</h3>
        <button
          onClick={() => setShowForm(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Donation Date *
            </label>
            <input
              type="date"
              name="donationDate"
              value={formData.donationDate}
              onChange={handleInputChange}
              max={getTodayDate()}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Units Donated *
            </label>
            <select
              name="units"
              value={formData.units}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              required
            >
              <option value={1}>1 Unit (450ml)</option>
              <option value={2}>2 Units (900ml)</option>
              <option value={3}>3 Units (1350ml)</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hospital/Blood Bank *
          </label>
          <input
            type="text"
            name="hospital"
            value={formData.hospital}
            onChange={handleInputChange}
            placeholder="Enter hospital or blood bank name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hospital Location
          </label>
          <input
            type="text"
            name="hospitalLocation"
            value={formData.hospitalLocation}
            onChange={handleInputChange}
            placeholder="City, District"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Urgency Level
            </label>
            <select
              name="urgency"
              value={formData.urgency}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="normal">Normal</option>
              <option value="urgent">Urgent</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Certificate Number
            </label>
            <input
              type="text"
              name="certificateNumber"
              value={formData.certificateNumber}
              onChange={handleInputChange}
              placeholder="Optional certificate number"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>

        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="isEmergency"
              checked={formData.isEmergency}
              onChange={handleInputChange}
              className="rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              This was an emergency donation (+75 bonus points)
            </span>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Additional Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            placeholder="Any additional details about the donation..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-blue-800 mb-2">Points Calculation:</h4>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• Base donation: 100 points</li>
            <li>• Urgent donation: +25 points</li>
            <li>• Critical donation: +50 points</li>
            <li>• Emergency donation: +75 points</li>
            <li>• Extra units: +25 points per additional unit</li>
          </ul>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => setShowForm(false)}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
          >
            {loading ? 'Recording...' : 'Record Donation'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DonationRecorder;
